import "./NutrientUnitsKPI.css";

import { ProjectIssuanceConstants } from "../../../../constants";
import { formatNumber, NUTRIENT_NITROGEN, NUTRIENT_PHOSPHORUS } from "../../../../utils";
import { GenericTooltip } from "../../../forms";

interface NutrientUnitsKPIProps {
  nitrogenUnits: number;
  phosphorusUnits: number;
}

const UNIT_LABEL = ProjectIssuanceConstants.ISSUANCE_NUTRIENT_UNIT_LABEL;

/**
 * The public project page's combined nutrient units tile: ONE figure for the project's whole
 * nutrient inventory, split proportionally between its two nutrients.
 *
 * Modelled on VerifiedPendingKPI, the carbon units tile beside it, rather than on BNGUnitsKPI: the
 * design note on this tile (Projects | Seed - Hub, node 4084-31649) says the figure is a single
 * measurement split between nitrogen and phosphorus and operates the way the carbon units KPI does.
 * BNGUnitsKPI renders one figure per unit type and drops any type with no units, which is a
 * per-measurement breakdown - the opposite shape, and it would silently delete the zero band a
 * nitrogen-only project exists to show.
 *
 * The bands take the KPI array's own green pair rather than the inventory chart's per-nutrient
 * palettes. The tile sits beside VerifiedPendingKPI and BNGUnitsKPI, which both split a single
 * figure into green bands, and it is read as one of those three - the chart's blue/orange identifies
 * a nutrient across a chart's bars, which is a different job on a different surface.
 */
export const NutrientUnitsKPI = ({ nitrogenUnits, phosphorusUnits }: NutrientUnitsKPIProps): JSX.Element => {
  const total = nitrogenUnits + phosphorusUnits;

  // Nitrogen then Phosphorus - alphabetical by quantity type, the order the NN schema enumerates
  // them and the order the inventory chart draws its bars in.
  // Lighter shade first, as the two tiles beside this one order their bands.
  const nutrients = [
    { shade: "Nitrogen", label: NUTRIENT_NITROGEN, units: nitrogenUnits },
    { shade: "Phosphorus", label: NUTRIENT_PHOSPHORUS, units: phosphorusUnits },
  ];

  /*
    Mirrors VerifiedPendingKPI's "PIUs only" / "PIUs and WCUs" pair. A project with neither nutrient
    is not rendered at all (KPIGroup gates it), so it takes the both-named wording rather than a
    fourth string.
  */
  let subLabel = `${NUTRIENT_NITROGEN} and ${NUTRIENT_PHOSPHORUS} (${UNIT_LABEL})`;
  if (nitrogenUnits > 0 && phosphorusUnits === 0) subLabel = `${NUTRIENT_NITROGEN} only (${UNIT_LABEL})`;
  if (phosphorusUnits > 0 && nitrogenUnits === 0) subLabel = `${NUTRIENT_PHOSPHORUS} only (${UNIT_LABEL})`;

  return (
    <div className="NutrientUnitsContainer">
      <div className="NutrientUnitsGraph">
        {nutrients.map(({ shade, label, units }) => (
          <GenericTooltip
            key={label}
            placement="right"
            content={
              <div className="NutrientUnitsTooltipContainer">
                <p className="body3">{`${formatNumber(units)} ${UNIT_LABEL} ${label}`}</p>
              </div>
            }
          >
            {/*
              A nutrient the project has none of keeps its band in the DOM as a 2px rule rather than
              losing it, so the tile says "this project has no phosphorus" instead of looking like a
              single-nutrient design. `total` guards the division for the both-zero case.
            */}
            <div
              className={`NutrientUnitsGraphBand ${shade} ${units > 0 ? "" : "NutrientUnitsGraphBand_empty"}`}
              style={units > 0 && total > 0 ? { flex: (units / total) * 100 } : undefined}
            />
          </GenericTooltip>
        ))}
      </div>
      <div className="NutrientUnitsHeaderArea">
        <h2>{formatNumber(total)}</h2>
        <p className="body2">
          {NUTRIENT_NITROGEN} &amp; {NUTRIENT_PHOSPHORUS} units
        </p>
        <p className="body3">{subLabel}</p>
      </div>
    </div>
  );
};
